/**
 * Skins (user-selectable UI themes, docs/themes/README.md). Imported as
 * `@velora/contracts/skins`.
 *
 * A skin is chosen per user and saved server-side (`ShellPreferences.skin`,
 * namespace `shell` of `user_preference`). With no saved choice the skin
 * follows the practice default, and with no practice default it follows
 * the PMS the user's office is synced from (`skinForPms`), so a Dentrix
 * office opens in Dentrix Familiar and an office with no PMS in
 * Velora. Users may override single layers (tokens, layout, terms, keymap)
 * under Settings > Appearance > Advanced.
 *
 * `SKIN_IDS` mirrors `@velora/tokens` (a web test keeps the two equal; the
 * contracts package has no runtime dependencies besides zod).
 */
import { z } from 'zod';

export const SKIN_IDS = ['velora', 'dentrix', 'eaglesoft', 'opendental'] as const;
export const SkinIdSchema = z.enum(SKIN_IDS);
export type SkinId = z.infer<typeof SkinIdSchema>;

export const DEFAULT_SKIN: SkinId = 'velora';

export function isSkinId(value: unknown): value is SkinId {
  return typeof value === 'string' && (SKIN_IDS as readonly string[]).includes(value);
}

/**
 * The mode names users see, exactly as in the master brief section 7.
 * Nominative use only: no vendor logo, artwork or wordmark styling.
 */
export const SKIN_LABELS: Record<SkinId, string> = {
  velora: 'Velora Modern',
  dentrix: 'Dentrix Familiar',
  opendental: 'Open Dental Familiar',
  eaglesoft: 'Eaglesoft Familiar',
};

/** Display order in the picker and onboarding (brief section 7). */
export const SKIN_ORDER: readonly SkinId[] = ['dentrix', 'opendental', 'eaglesoft', 'velora'];

export const APPEARANCE_MODES = ['light', 'dark'] as const;
export type AppearanceMode = (typeof APPEARANCE_MODES)[number];

/**
 * Light or dark is a separate toggle combined with the mode, remembered per
 * mode: Velora Modern starts dark (ADR-0007), the familiar modes start
 * light like the products they evoke.
 */
export const SKIN_DEFAULT_MODE: Record<SkinId, AppearanceMode> = {
  velora: 'dark',
  dentrix: 'light',
  opendental: 'light',
  eaglesoft: 'light',
};

export const SKIN_LAYOUTS = ['modern', 'classic'] as const;
export type SkinLayout = (typeof SKIN_LAYOUTS)[number];

/** Per-layer overrides on top of the chosen skin (spec 7.1 "Advanced"). */
export const SkinOverridesSchema = z
  .object({
    tokens: SkinIdSchema,
    layout: z.enum(SKIN_LAYOUTS),
    terms: SkinIdSchema,
    keymap: SkinIdSchema,
  })
  .partial();
export type SkinOverrides = z.infer<typeof SkinOverridesSchema>;

/** Which imaging screens an office uses (spec 6, 7.6). Per office, not per user. */
export const IMAGING_SYSTEMS = ['dexis', 'eaglesoft-imaging', 'velora'] as const;
export const ImagingSystemSchema = z.enum(IMAGING_SYSTEMS);
export type ImagingSystem = z.infer<typeof ImagingSystemSchema>;

/** Practice-level skin settings, read in the shell bootstrap. */
export const SkinDefaultsSchema = z.object({
  /** Skin for users with no saved choice; `null` means "follow the office's PMS". */
  defaultSkin: SkinIdSchema.nullable(),
  /** `null` means the skin's own default (see `imagingSkinFor`). */
  imagingSystem: ImagingSystemSchema.nullable(),
});
export type SkinDefaults = z.infer<typeof SkinDefaultsSchema>;

export const SkinDefaultsPatchSchema = SkinDefaultsSchema.partial();
export type SkinDefaultsPatch = z.infer<typeof SkinDefaultsPatchSchema>;

/** Onboarding: "Which practice software do you know best?" */
export const KNOWN_PMS_CHOICES = ['dentrix', 'opendental', 'eaglesoft', 'none'] as const;
export type KnownPms = (typeof KNOWN_PMS_CHOICES)[number];

const PMS_SKIN: Record<string, SkinId> = {
  dentrix: 'dentrix',
  eaglesoft: 'eaglesoft',
  opendental: 'opendental',
};

/** The mode to suggest for the PMS an employee knows (`none`: Velora Modern). */
export function suggestSkin(known: KnownPms): SkinId {
  return known === 'none' ? DEFAULT_SKIN : known;
}

/** The skin an office's PMS implies (`null` or unknown PMS: Velora). */
export function skinForPms(pmsSystem: string | null | undefined): SkinId {
  return (pmsSystem && PMS_SKIN[pmsSystem]) || DEFAULT_SKIN;
}

/**
 * The effective skin: the user's choice, else the practice default, else
 * the PMS of the office in view (first office when "All offices").
 */
export function resolveSkin(input: {
  userSkin?: SkinId | null | undefined;
  practiceDefault?: SkinId | null | undefined;
  officePms?: string | null | undefined;
}): SkinId {
  return input.userSkin ?? input.practiceDefault ?? skinForPms(input.officePms);
}

/** Imaging skin: the office setting wins; otherwise Eaglesoft offices see DEXIS too (spec 6.2). */
export function imagingSkinFor(skin: SkinId, office: ImagingSystem | null): ImagingSystem {
  if (office) return office;
  return skin === 'velora' ? 'velora' : 'dexis';
}
